import _ from "lodash";
import { FeatureTypes } from "entity-app/constants";
import {
  DateFilter,
  DateUtils,
  DATE_FORMAT_MMDDYYYY_HHMM_A_Z
} from "utils/dateutils";
import { ColumnMenuTab, ColDef } from "ag-grid-enterprise";
import CustomDateSetFilter from "entity-app/custom-filters/custom-date-filter";
import { numberFilterParams } from "components/cell-editor/numberCellEditor";
import { EnumOptionsMap } from "hooks/enum-values";
import { ContextColumnConfigType } from "../models";
import {
  CUSTOM_FIELD_PREFIX,
  CustomReportsI18n,
  LINKED_SUBMITTAL_DESCRIPTION_KEY,
  LINKED_SUBMITTAL_FINAL_RESPONSE_KEY,
  LINKED_SUBMITTAL_TITLE_KEY,
  LINKED_SUBMITTALS_KEY,
  MATERIAL_DESCRIPTION_KEY,
  MATERIAL_TITLE_KEY
} from "../custom-reports-constants";

export type MaterialCustomColumn = {
  field: string;
  field_type: string;
  headerName?: string;
};

const headerValueGetter = (
  params: any,
  fieldKey: string,
  parentKey?: string
) => {
  try {
    if (!params?.context?.columnConfig) {
      return "";
    }
    let fields = params?.context?.columnConfig;

    if (parentKey) {
      fields = fields?.find(
        (column: any) => column.field_key === parentKey
      )?.fields;
    }

    const foundColumn = fields?.find(
      (column: any) =>
        canonicalDateBlockFieldKey(column.field_key) ===
        canonicalDateBlockFieldKey(fieldKey)
    );
    return foundColumn?.caption_en || fieldKey;
  } catch (err) {
    console.error("Error ", fieldKey, err);
  }
  return "";
};

const flattenData = (data: any, inlineModeForFeatures: number[]) => {
  if (!data) return [];
  return data.flatMap((item: any, materialIndex: number) => {
    const linkedSubmittals = item.linked_submittals || [];
    const linkedActivities = item.linked_activities || [];

    const lengths = [];

    if (inlineModeForFeatures.includes(FeatureTypes.SUBMITTALS)) {
      lengths.push(linkedSubmittals.length);
    }

    if (inlineModeForFeatures.includes(FeatureTypes.ACTIVITIES)) {
      lengths.push(linkedActivities.length);
    }

    const maxLength = lengths.length > 0 ? Math.max(...lengths) : 0;

    if (maxLength === 0) {
      return [item];
    }

    const baseKey = item?.id != null ? String(item.id) : `mi${materialIndex}`;

    return Array.from({ length: maxLength }, (__, i) => ({
      ...item,
      shodowRow: i !== 0,
      ...linkedSubmittals[i],
      ...linkedActivities[i],
      __rowInstanceKey: `${baseKey}-${i}`
    }));
  });
};

const shouldSpanRow = ({
  path,
  nodeAData,
  nodeBData
}: {
  path: string;
  nodeAData: any;
  nodeBData: any;
}) => {
  if (nodeAData.id !== nodeBData.id) return false;

  if (path === "linked_activities") {
    const activityAIds = _.get(nodeAData, path)?.map(
      (activity: any) => activity.id
    );
    const activityBIds = _.get(nodeBData, path)?.map(
      (activity: any) => activity.id
    );
    return _.isEqual(activityAIds, activityBIds);
  }

  return _.get(nodeAData, path) === _.get(nodeBData, path);
};

/**
 * Map mistaken tool-panel / saved keys to real grid colIds (avoid duplicate Durations entries).
 * e.g. offsets__offset_4_name → offsets__name_offset_4
 */
const canonicalDateBlockFieldKey = (fieldKey: string): string => {
  const wrongOffName = /^offsets__offset_(\d+)_name$/.exec(fieldKey);
  if (wrongOffName) return `offsets__name_offset_${wrongOffName[1]}`;
  const wrongMsName = /^milestones__milestone_(\d+)_name$/.exec(fieldKey);
  if (wrongMsName) return `milestones__name_milestone_${wrongMsName[1]}`;
  return fieldKey;
};

// Update visibility in columnConfig
const updateColumnVisibility = (
  config: ContextColumnConfigType[],
  fieldKey: string,
  visible: boolean
): any[] =>
  config.map((col) => {
    const want = canonicalDateBlockFieldKey(fieldKey);
    if (
      typeof col.field_key === "string" &&
      canonicalDateBlockFieldKey(col.field_key) === want
    ) {
      return { ...col, field_key: want, visibility: visible };
    }
    if (col.fields) {
      return {
        ...col,
        fields: updateColumnVisibility(col.fields, fieldKey, visible)
      };
    }
    return col;
  });

// Update order in columnConfig
const updateColumnOrder = (
  config: ContextColumnConfigType[],
  orderedKeys: string[]
): any[] => {
  return config
    .map((col) => {
      if (col.fields) {
        return {
          ...col,
          fields: updateColumnOrder(col.fields, orderedKeys),
          order: orderedKeys.indexOf(col.field_key) + 1
        };
      }
      return { ...col, order: orderedKeys.indexOf(col.field_key) + 1 };
    })
    .sort((a, b) => a.order - b.order);
};

const translateVLargeMaterial = (materials: Array<any>) => {
  return materials.map((m) => {
    return {
      ...m,
      planned_first_milestone_date: m.planned_milestone_1,
      actual_first_milestone_date: m.actual_milestone_1,
      actual_last_milestone_date: m.actual_last_milestone_date,
      project_locations: m.project_locations?.map((loc: string) => ({
        id: loc,
        name: loc,
        path: loc
      })),
      tasks:
        m.linked_activities?.map((t: any) => ({
          task_id: t.linked_activities__id,
          task_name: t.linked_activities__name,
          offset_duration: t.linked_activities__offset_duration,
          linked_offset_date: t.linked_activities__linked_offset_date,
          activity_id: t.linked_activities__number,
          critical: t.linked_activities__is_critical,
          total_slack: t.linked_activities__total_slack,
          is_critical: t.linked_activities__is_critical,
          total_slack_computed: null,
          progress: t.linked_activities__progress,
          start_date: t.linked_activities__start_date,
          end_date: t.linked_activities__end_date,
          linked_to_end_date: t.linked_activities__linked_to_end_date,
          actual_start_date: t.linked_activities__start_date,
          actual_finish_date: t.linked_activities__end_date,
          deleted_from_source: t.linked_activities__deleted_from_source
        })) || [],

      linked_items: m.linked_submittals?.map((s: any) => ({
        ...s,

        id: s.linked_submittals__id,
        feature_type: s.linked_submittals__feature_type,
        sequence_id: s.linked_submittals__sequence_id,
        name: s.linked_submittals__title,
        milestone_state: s.linked_submittals__status,
        risk_level: s.linked_submittals__risk,
        spec_section: s.linked_submittals__spec_sections,
        milestones: s.linked_submittals__milestones?.map((x: any) => ({
          actual_date: x.actual,
          name: x.name,
          projected_date: x.projected,
          planned_date: x.planned,
          sequence_no: x.sequence_no,
          milestone_action: x.milestone_action,
          is_active: x.is_active,
          milestone_type: x.milestone_type
        })),
        revision: s.linked_submittals__revision,
        deleted_from_source: false,
        workflow_status: s.linked_submittals__workflow_status,
        type: s.linked_submittals__type
      }))
    };
  });
};

const NESTED_FIELD_PREFIXES: any = {
  linked_submittals: `${LINKED_SUBMITTALS_KEY}__`
  // linked_activities: "activity__"
};

// Split the saved config into root columns and the nested blocks that get
// flattened into prefixed grid columns in inline view.
const partitionColumns = (json1: any, viewMode: string | undefined) => {
  const rootMap = new Map();
  const nestedCols: any = {};

  json1.forEach((col: any) => {
    if (
      viewMode === "inline" &&
      col.fields &&
      NESTED_FIELD_PREFIXES[col.field_key]
    ) {
      nestedCols[col.field_key] = _.cloneDeep(col); // clone nested block
    } else {
      rootMap.set(col.field_key, {
        ...col,
        ...(col.fields
          ? { fields: col.fields.map((f: any) => ({ ...f })) }
          : {})
      });
    }
  });

  return { rootMap, nestedCols };
};

// Consume the run of consecutive json2 keys sharing `prefix`, returning the
// matching sub-columns and the index of the last key consumed.
const collectNestedFields = (
  json2: any,
  start: number,
  prefix: string,
  nestedBlock: any
) => {
  const fields: any[] = [];
  let i = start;
  while (i < json2.length && json2[i].startsWith(prefix)) {
    // Use full key - field_key in nestedBlock.fields includes the prefix
    const fullKey = json2[i];
    const subCol = nestedBlock.fields.find((f: any) => f.field_key === fullKey);
    if (subCol) fields.push({ ...subCol });
    i++;
  }
  return { fields, lastIndex: i - 1 };
};

const renumberColumns = (columns: any[]) => {
  columns.forEach((col: any, idx: number) => {
    col.order = idx + 1;
    if (col.fields) {
      col.fields.forEach(
        (subCol: any, subIdx: number) => (subCol.order = subIdx + 1)
      );
    }
  });
};

const reorderJSON1FromJSON2 = (
  json1: any,
  json2: any,
  viewMode: string | undefined
) => {
  const { rootMap, nestedCols } = partitionColumns(json1, viewMode);

  const result: any[] = [];
  let newOrder = 1;
  const addedRootKeys = new Set();

  // Walked with an explicit cursor because a nested block consumes a run of
  // json2 keys in one step rather than one key per iteration.
  let i = 0;
  while (i < json2.length) {
    const key = json2[i];

    // Check if this key belongs to any nested block
    const nestedEntry = Object.entries(NESTED_FIELD_PREFIXES).find(
      ([, prefix]) => key.startsWith(prefix as string)
    );
    const nestedBlock = nestedEntry ? nestedCols[nestedEntry[0]] : undefined;

    if (nestedEntry && nestedBlock) {
      const { fields, lastIndex } = collectNestedFields(
        json2,
        i,
        nestedEntry[1] as string,
        nestedBlock
      );

      fields.forEach((f: any, idx: number) => (f.order = idx + 1));
      nestedBlock.fields = fields;
      nestedBlock.order = newOrder++;
      result.push(nestedBlock);

      i = lastIndex + 1;
      continue;
    }

    if (!nestedEntry) {
      const col = rootMap.get(key);
      if (col && !addedRootKeys.has(col.field_key)) {
        col.order = newOrder++;
        result.push(col);
        addedRootKeys.add(col.field_key);
      }
    }

    i += 1;
  }

  // Append remaining root fields not in JSON-2
  rootMap.forEach((col: any) => {
    if (!addedRootKeys.has(col.field_key)) {
      col.order = newOrder++;
      result.push(col);
    }
  });

  // Final order recalculation for nested fields
  renumberColumns(result);

  return result;
};

const flattenJSON1ToJSON2 = (json1: any, viewMode: string | undefined) => {
  if (!viewMode) return [];

  const result: any[] = [];
  let currentOrder = 1;

  json1.forEach((col: any) => {
    if (
      viewMode === "inline" &&
      col.fields &&
      NESTED_FIELD_PREFIXES[col.field_key]
    ) {
      col.fields.forEach((subCol: any) => {
        result.push({
          colId: subCol.field_key, // Use field_key directly - it already includes the prefix
          hide: !subCol.visibility,
          order: currentOrder++
        });
      });
    } else {
      result.push({
        colId: col.field_key,
        hide: !col.visibility,
        order: currentOrder++
      });
    }
  });

  return result;
};

/** Stable across re-runs so rowData updates (e.g. column config save) do not reshuffle group rows. */
const stableUnassignedPlaceholderId = (item: any, role: string) => {
  const key = item?.id ?? item?.material_id ?? "unknown";
  return `__unassigned__${role}__${String(key)}`;
};

const applyUnassignedDefaults = (
  materials: any[],
  groupByField: string | undefined,
  columnConfig: any[] | null
) => {
  if (!groupByField) return materials;

  const fieldConfig: Record<
    string,
    {
      defaultLabel: string;
      applyDefault: (item: any, unassignedLabel: string) => any;
    }
  > = {
    gc_representative: {
      defaultLabel: "GC Representative",
      applyDefault: (item, label) => ({
        ...item,
        gc_representative: item.gc_representative || {
          id: stableUnassignedPlaceholderId(item, "gc_representative"),
          name: `Unassigned ${label}`,
          email: ""
        }
      })
    },
    responsible_contractor_name: {
      defaultLabel: "Responsible Contractor",
      applyDefault: (item, label) => ({
        ...item,
        responsible_contractor_id:
          item.responsible_contractor_id ||
          stableUnassignedPlaceholderId(item, "responsible_contractor"),
        responsible_contractor_name:
          item.responsible_contractor_name || `Unassigned ${label}`
      })
    },
    assignee_user: {
      defaultLabel: "Assignee User",
      applyDefault: (item, label) => ({
        ...item,
        assignee_user: item.assignee_user || {
          id: stableUnassignedPlaceholderId(item, "assignee_user"),
          name: `Unassigned ${label}`,
          email: ""
        }
      })
    },
    spec_sections: {
      defaultLabel: "Spec Section",
      applyDefault: (item) => ({
        ...item,
        spec_sections: item.spec_sections || ["Unassigned Spec Section"]
      })
    }
  };

  const config = fieldConfig[groupByField];
  if (!config) return materials;

  const columnHeader =
    columnConfig?.find((col: any) => col.field_key === groupByField)
      ?.caption_en || config.defaultLabel;

  return materials.map((item: any) => config.applyDefault(item, columnHeader));
};

const dateCellValueGetter = (params: any) => {
  const dateValue = params.data?.[params.colDef.field as string];
  return dateValue ? DateUtils.format(dateValue) : undefined;
};

const dateTimeCellValueGetter = (params: any) => {
  const dateValue = params.data?.[params.colDef.field as string];
  return dateValue
    ? DateUtils.format(dateValue, DATE_FORMAT_MMDDYYYY_HHMM_A_Z)
    : undefined;
};

const updateGridSortConfig = ({
  sortState,
  groupByField,
  groupByOptions,
  columnConfig
}: {
  sortState: { sort: "asc" | "desc" | null; field_key: string }[];
  groupByField: string;
  groupByOptions: string[];
  columnConfig: ContextColumnConfigType[];
}) => {
  let clonedSortState = _.cloneDeep(sortState);
  if (groupByField !== "none") {
    // check if sortState has a sort for any of the groupByOptions
    const hasSort = clonedSortState.some((sort) =>
      groupByOptions.includes(sort.field_key)
    );
    if (hasSort) {
      // remove the sort for any of the groupByOptions
      clonedSortState = clonedSortState.filter(
        (sort) => !groupByOptions.includes(sort.field_key)
      );
      // remove array item with field_key as "group_by_column"
      clonedSortState = clonedSortState.filter(
        (sort) => sort.field_key !== "group_by_column"
      );
    }

    // insert two new items in the clonedSortState at the beginning
    clonedSortState.unshift({
      field_key: groupByField,
      sort: "asc"
    });
    clonedSortState.unshift({
      field_key: "group_by_column",
      sort: "asc"
    });
    return clonedSortState;
  }
  // remove array item with field_key as "group_by_column"
  clonedSortState = clonedSortState.filter(
    (sort) => sort.field_key !== "group_by_column"
  );
  // remove array item with field_key matching any of the groupByOptions
  clonedSortState = clonedSortState.filter(
    (sort) => !groupByOptions.includes(sort.field_key)
  );

  // get item from columnConfig whose order is lowest, ignore the item with field_key as "group_by_column"
  const lowestOrderColumn = [...columnConfig]
    ?.filter(
      (col) =>
        col.field_key !== "group_by_column" && col.field_key !== "description"
    )
    .sort((a, b) => a.order - b.order);

  const columnWithVisibilityEnabled = lowestOrderColumn.find(
    (col) => col.visibility
  );

  if (columnWithVisibilityEnabled) {
    clonedSortState.unshift({
      field_key: columnWithVisibilityEnabled.field_key,
      sort: "asc"
    });
  }

  return clonedSortState;
};

const headerTooltipValueGetter = (params: any) => {
  return params.value || "";
};

const getItemBySequenceNo = (
  array: any[] | undefined | null,
  sequenceNo: number
) => {
  if (!Array.isArray(array) || array.length === 0) return undefined;
  return array.find((item: any) => item?.sequence_no === sequenceNo);
};

const getColumnsForDateBlock = (
  reportItems: { milestones: any[]; offsets: any[] }[]
) => {
  const dbColsDefaultProperties = {
    // Needed so leaf date-block columns can appear in Columns Tool Panel
    // when we apply a custom tree layout via setColumnLayout.
    suppressColumnsToolPanel: false,
    suppressMovable: true,
    menuTabs: ["filterMenuTab"] as ColumnMenuTab[],
    editable: false,
    width: 180,
    lockPosition: "right" as const
  };

  const dateBlockGroupCols: ColDef[] = [
    {
      colId: "milestones",
      headerName: "Milestones",
      width: 0,
      maxWidth: 0,
      headerClass: "!p-0 !w-0",
      filter: false,
      editable: false,
      suppressMovable: true,
      getQuickFilterText: () => "",
      // hide: true,
      lockPosition: "right"
    },
    {
      colId: "offsets",
      headerName: "Durations",
      width: 0,
      maxWidth: 0,
      headerClass: "!p-0 !w-0",
      filter: false,
      editable: false,
      suppressMovable: true,
      getQuickFilterText: () => "",
      // hide: true,
      lockPosition: "right"
    }
  ];

  const dateBlockColumnDefs: ColDef[] = [];

  const maxNoOfMilestones = reportItems.reduce((max, item) => {
    return Math.max(max, item.milestones?.length || 0);
  }, 0);

  const maxNoOfOffsets = reportItems.reduce((max, item) => {
    return Math.max(max, item.offsets?.length || 0);
  }, 0);

  const maxGroupCount = Math.max(maxNoOfMilestones, maxNoOfOffsets);

  for (let i = 0; i < maxGroupCount; i += 1) {
    const groupNumber = i + 1;

    // Add milestone columns for this group
    if (i < maxNoOfMilestones) {
      dateBlockColumnDefs.push(
        {
          colId: `milestones__name_milestone_${groupNumber}`,
          headerName: `Milestone Name ${groupNumber}`,
          headerTooltip: `Milestone Name ${groupNumber}`,
          ...dbColsDefaultProperties,
          valueGetter: (params: any) => {
            try {
              const milestones = params?.data?.milestones ?? [];
              return getItemBySequenceNo(milestones, groupNumber)?.name;
            } catch {
              return "";
            }
          }
        },
        {
          colId: `milestones__planned_milestone_${groupNumber}`,
          headerName: `Planned Date ${groupNumber}`,
          headerTooltip: `Planned Date ${groupNumber}`,
          ...dbColsDefaultProperties,
          valueGetter: (params: any) => {
            try {
              const milestones = params?.data?.milestones ?? [];
              const dt = getItemBySequenceNo(milestones, groupNumber)?.planned;

              return dt ? DateUtils.format(dt) : undefined;
            } catch {
              return "";
            }
          },
          filter: CustomDateSetFilter,
          comparator: DateFilter.comparator
        },
        {
          colId: `milestones__projected_milestone_${groupNumber}`,
          headerName: `Projected Date ${groupNumber}`,
          headerTooltip: `Projected Date ${groupNumber}`,
          ...dbColsDefaultProperties,
          valueGetter: (params: any) => {
            try {
              const milestones = params?.data?.milestones ?? [];
              const dt = getItemBySequenceNo(
                milestones,
                groupNumber
              )?.projected;
              return dt ? DateUtils.format(dt) : undefined;
            } catch {
              return "";
            }
          },
          filter: CustomDateSetFilter,
          comparator: DateFilter.comparator
        },
        {
          colId: `milestones__actual_milestone_${groupNumber}`,
          headerName: `Actual Date ${groupNumber}`,
          headerTooltip: `Actual Date ${groupNumber}`,
          ...dbColsDefaultProperties,
          valueGetter: (params: any) => {
            try {
              const milestones = params?.data?.milestones ?? [];
              const dt = getItemBySequenceNo(milestones, groupNumber)?.actual;
              return dt ? DateUtils.format(dt) : undefined;
            } catch {
              return "";
            }
          },
          filter: CustomDateSetFilter,
          comparator: DateFilter.comparator
        }
      );
    }

    // Add offset columns for this group (grouped with milestones)
    if (i < maxNoOfOffsets) {
      dateBlockColumnDefs.push(
        {
          colId: `offsets__name_offset_${groupNumber}`,
          headerName: `Duration Name ${groupNumber}`,
          headerTooltip: `Duration Name ${groupNumber}`,
          ...dbColsDefaultProperties,
          valueGetter: (params: any) => {
            try {
              const offsets = params?.data?.offsets ?? [];
              return getItemBySequenceNo(offsets, groupNumber)?.offset_name;
            } catch {
              return "";
            }
          }
        },
        {
          colId: `offsets__offset_${groupNumber}_duration`,
          headerName: `Duration ${groupNumber}`,
          headerTooltip: `Duration ${groupNumber}`,
          ...dbColsDefaultProperties,
          valueGetter: (params: any) => {
            try {
              const offsets = params?.data?.offsets ?? [];
              return getItemBySequenceNo(offsets, groupNumber)?.duration;
            } catch {
              return "";
            }
          },
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams
        }
      );
    }
  }

  const dateBlockColumnIds = {
    milestones: dateBlockColumnDefs
      .map((col) => col.colId)
      // colId is always an explicit string on every pushed def, so col?. never short-circuits
      /* v8 ignore start */
      .filter((col): col is string => col?.includes("milestone_") ?? false),
    /* v8 ignore stop */
    offsets: dateBlockColumnDefs
      .map((col) => col.colId)
      // colId is always an explicit string on every pushed def, so col?. never short-circuits
      /* v8 ignore start */
      .filter((col): col is string => col?.includes("offset_") ?? false)
    /* v8 ignore stop */
  };

  return {
    dateBlockGroupCols,
    dateBlockColumnDefs,
    dateBlockColumnIds
  };
};

const getDateBlockMaxIndexFromColIds = (colIds: string[]) => {
  let max = 0;
  colIds.forEach((id) => {
    const durationMatch = /_offset_(\d+)_duration$/.exec(id);
    if (durationMatch) {
      const n = Number(durationMatch[1]);
      // durationMatch[1] is a (\d+) capture, so Number() is never NaN
      /* v8 ignore next */
      if (!Number.isNaN(n)) max = Math.max(max, n);
      return;
    }
    const match = /_(\d+)$/.exec(id);
    if (!match) return;
    const n = Number(match[1]);
    // match[1] is a (\d+) capture, so Number() is never NaN
    /* v8 ignore next */
    if (!Number.isNaN(n)) max = Math.max(max, n);
  });
  return max;
};

const getSidebarColumnLayoutForCustomReportDateBlock = (dateBlockColumns: {
  dateBlockColumnIds: { milestones: string[]; offsets: string[] };
}) => {
  const nonDraggableToolPanelClass = "md-tree-non-draggable";
  const milestonesCount = getDateBlockMaxIndexFromColIds(
    dateBlockColumns.dateBlockColumnIds.milestones
  );
  const offsetsCount = getDateBlockMaxIndexFromColIds(
    dateBlockColumns.dateBlockColumnIds.offsets
  );

  const milestonesGroupCol: any = {
    headerName: "Milestones",
    groupId: "milestones_group",
    suppressMovable: true,
    openByDefault: false,
    toolPanelClass: nonDraggableToolPanelClass,
    children: []
  };
  const durationsGroupCol: any = {
    headerName: "Durations",
    groupId: "durations_group",
    suppressMovable: true,
    openByDefault: false,
    toolPanelClass: nonDraggableToolPanelClass,
    children: []
  };

  for (let i = 1; i <= milestonesCount; i += 1) {
    milestonesGroupCol.children.push({
      headerName: `Milestone ${i}`,
      groupId: `milestone_group_${i}`,
      suppressMovable: true,
      openByDefault: false,
      toolPanelClass: nonDraggableToolPanelClass,
      children: [
        {
          colId: `milestones__name_milestone_${i}`,
          headerName: `Milestone Name ${i}`,
          suppressMovable: true,
          toolPanelClass: nonDraggableToolPanelClass
        },
        {
          colId: `milestones__planned_milestone_${i}`,
          headerName: `Planned Date ${i}`,
          suppressMovable: true,
          toolPanelClass: nonDraggableToolPanelClass
        },
        {
          colId: `milestones__projected_milestone_${i}`,
          headerName: `Projected Date ${i}`,
          suppressMovable: true,
          toolPanelClass: nonDraggableToolPanelClass
        },
        {
          colId: `milestones__actual_milestone_${i}`,
          headerName: `Actual Date ${i}`,
          suppressMovable: true,
          toolPanelClass: nonDraggableToolPanelClass
        }
      ]
    });
  }

  for (let i = 1; i <= offsetsCount; i += 1) {
    durationsGroupCol.children.push({
      headerName: `Duration ${i}`,
      groupId: `duration_group_${i}`,
      suppressMovable: true,
      openByDefault: false,
      toolPanelClass: nonDraggableToolPanelClass,
      children: [
        {
          colId: `offsets__name_offset_${i}`,
          headerName: `Duration Name ${i}`,
          suppressMovable: true,
          toolPanelClass: nonDraggableToolPanelClass
        },
        {
          colId: `offsets__offset_${i}_duration`,
          headerName: `Duration ${i}`,
          suppressMovable: true,
          toolPanelClass: nonDraggableToolPanelClass
        }
      ]
    });
  }

  return {
    sidebarColumnLayout: [milestonesGroupCol, durationsGroupCol]
  };
};

/** Merge duplicate date-block leaves under milestones / offsets (same semantic column, two keys). */
const dedupeDateBlockFieldsInColumnConfig = (config: any[]): any[] => {
  const dedupeFields = (fields: any[] | undefined): any[] | undefined => {
    if (!Array.isArray(fields) || fields.length === 0) return fields;
    const merged = new Map<string, any>();
    for (const f of fields) {
      const canon = canonicalDateBlockFieldKey(f.field_key);
      const row = { ...f, field_key: canon };
      if (!merged.has(canon)) {
        merged.set(canon, row);
      } else {
        const prev = merged.get(canon)!;
        merged.set(canon, {
          ...prev,
          ...row,
          field_key: canon,
          visibility:
            row.visibility !== undefined ? row.visibility : prev.visibility,
          order: Math.min(Number(prev.order ?? 999), Number(row.order ?? 999)),
          caption_en: prev.caption_en || row.caption_en,
          original_caption_en:
            prev.original_caption_en || row.original_caption_en
        });
      }
    }
    return Array.from(merged.values()).sort(
      (a, b) => Number(a.order ?? 0) - Number(b.order ?? 0)
    );
  };

  return config.map((col: any) => {
    if (col.field_key === "milestones" && Array.isArray(col.fields)) {
      return { ...col, fields: dedupeFields(col.fields) };
    }
    if (col.field_key === "offsets" && Array.isArray(col.fields)) {
      return { ...col, fields: dedupeFields(col.fields) };
    }
    return col;
  });
};

/** Read visibility from saved config: grid uses milestones__/offsets__, nested keys may be unprefixed. */
const resolveDateBlockVisibilityForGridCol = (
  gridColId: string,
  visibilityByKey: Map<string, boolean>
): boolean | undefined => {
  const canonGrid = canonicalDateBlockFieldKey(gridColId);
  for (const k of [gridColId, canonGrid]) {
    if (visibilityByKey.has(k)) return visibilityByKey.get(k);
  }
  if (canonGrid.startsWith("milestones__")) {
    const legacy = canonGrid.slice("milestones__".length);
    if (visibilityByKey.has(legacy)) return visibilityByKey.get(legacy);
  }
  if (canonGrid.startsWith("offsets__")) {
    const legacy = canonGrid.slice("offsets__".length);
    if (visibilityByKey.has(legacy)) return visibilityByKey.get(legacy);
  }
  return undefined;
};

type FieldType = ContextColumnConfigType["field_type"];

const toCustomFieldConfigType = (fieldType: string): FieldType => {
  if (fieldType === "date") return "date";
  if (fieldType === "number") return "number";
  if (fieldType === "user" || fieldType === "vendor") return "object";
  return "string";
};

const newHiddenColumn = (
  fieldKey: string,
  caption: string,
  fieldType: FieldType
): ContextColumnConfigType => ({
  field_key: fieldKey,
  caption_en: caption,
  original_caption_en: caption,
  order: 0,
  allow_rename: true,
  field_type: fieldType,
  visibility: false,
  customizable: true
});

const insertAfter = (
  columns: ContextColumnConfigType[],
  afterKey: string,
  column: ContextColumnConfigType
) => {
  const index = columns.findIndex((col) => col.field_key === afterKey);
  if (index === -1) columns.push(column);
  else columns.splice(index + 1, 0, column);
};

/**
 * Adds report columns introduced after a report was saved (material description,
 * linked submittal description / final response, material custom fields) as
 * hidden, renamable entries so they show up in the column selector and rename modal.
 */
const ensureReportColumnsInConfig = (
  config: ContextColumnConfigType[] | null | undefined,
  materialCustomColumns: MaterialCustomColumn[] = []
): ContextColumnConfigType[] | null | undefined => {
  if (!Array.isArray(config)) return config;

  const next: ContextColumnConfigType[] = config.map((col) =>
    col.fields
      ? { ...col, fields: col.fields.map((f) => ({ ...f })) }
      : { ...col }
  );

  const descriptionIndex = next.findIndex(
    (col) => col.field_key === MATERIAL_DESCRIPTION_KEY
  );
  if (descriptionIndex === -1) {
    insertAfter(
      next,
      MATERIAL_TITLE_KEY,
      newHiddenColumn(
        MATERIAL_DESCRIPTION_KEY,
        CustomReportsI18n.materialDescription,
        "string"
      )
    );
  } else if (!next[descriptionIndex].customizable) {
    // Description used to be a non-selectable hidden column; start it hidden.
    next[descriptionIndex] = {
      ...next[descriptionIndex],
      allow_rename: true,
      customizable: true,
      visibility: false
    };
  }

  const linkedSubmittals = next.find(
    (col) => col.field_key === LINKED_SUBMITTALS_KEY
  );
  if (linkedSubmittals && Array.isArray(linkedSubmittals.fields)) {
    const { fields } = linkedSubmittals;
    const submittalDescriptionIndex = fields.findIndex(
      (f) => f.field_key === LINKED_SUBMITTAL_DESCRIPTION_KEY
    );
    const submittalDescription =
      submittalDescriptionIndex === -1
        ? newHiddenColumn(
            LINKED_SUBMITTAL_DESCRIPTION_KEY,
            CustomReportsI18n.submittalDescription,
            "string"
          )
        : fields.splice(submittalDescriptionIndex, 1)[0];
    insertAfter(fields, LINKED_SUBMITTAL_TITLE_KEY, submittalDescription);
    if (
      !fields.some((f) => f.field_key === LINKED_SUBMITTAL_FINAL_RESPONSE_KEY)
    ) {
      fields.push(
        newHiddenColumn(
          LINKED_SUBMITTAL_FINAL_RESPONSE_KEY,
          CustomReportsI18n.finalResponse,
          "string"
        )
      );
    }
  }

  materialCustomColumns.forEach((customColumn) => {
    if (!customColumn?.field) return;
    if (next.some((col) => col.field_key === customColumn.field)) return;
    next.push(
      newHiddenColumn(
        customColumn.field,
        customColumn.headerName || customColumn.field,
        toCustomFieldConfigType(customColumn.field_type)
      )
    );
  });

  renumberColumns(next);
  return next;
};

const getCustomFieldValue = (
  data: any,
  customColumn: MaterialCustomColumn,
  enumOptionsMap?: EnumOptionsMap
) => {
  if (!data) return undefined;
  const key = customColumn.field.replace(CUSTOM_FIELD_PREFIX, "");
  const customFields = data._custom_fields ?? {};
  const raw = customFields[key] ?? data[customColumn.field];

  switch (customColumn.field_type) {
    case "date":
      return raw ? DateUtils.format(raw) : undefined;
    case "user":
      if (raw && typeof raw === "object") {
        return (
          `${raw.first_name ?? raw.name ?? ""} ${raw.last_name ?? ""}`.trim() ||
          undefined
        );
      }
      return raw ?? undefined;
    case "vendor":
      if (raw && typeof raw === "object") return raw.name ?? undefined;
      return raw ?? undefined;
    case "geolocation": {
      const geo = customFields[`${key}_obj`] ?? raw;
      if (geo && typeof geo === "object") {
        return geo.location_service?.label ?? geo.label ?? undefined;
      }
      return geo ?? undefined;
    }
    case "enum": {
      if (raw == null) return undefined;
      const option = enumOptionsMap?.[key]?.find((opt) => opt.id === raw);
      return option?.value ?? raw;
    }
    default:
      return raw ?? undefined;
  }
};

/** Final response may come as plain text or as the submittal log's response array. */
const getFinalResponseText = (value: any): string => {
  if (value == null) return "";
  if (Array.isArray(value)) {
    return value
      .map((item: any) =>
        typeof item === "string" ? item : (item?.response ?? "")
      )
      .filter(Boolean)
      .join(", ");
  }
  if (typeof value === "object") return value.response ?? "";
  return String(value);
};

export {
  ensureReportColumnsInConfig,
  getCustomFieldValue,
  getFinalResponseText,
  headerValueGetter,
  flattenData,
  shouldSpanRow,
  updateColumnVisibility,
  updateColumnOrder,
  translateVLargeMaterial,
  reorderJSON1FromJSON2,
  flattenJSON1ToJSON2,
  applyUnassignedDefaults,
  dateCellValueGetter,
  dateTimeCellValueGetter,
  updateGridSortConfig,
  headerTooltipValueGetter,
  getColumnsForDateBlock,
  getSidebarColumnLayoutForCustomReportDateBlock,
  resolveDateBlockVisibilityForGridCol,
  canonicalDateBlockFieldKey,
  dedupeDateBlockFieldsInColumnConfig
};
